/**
 * Hikari Vault - Storage Layer
 * =============================
 * localStorage cache + backend sync. Everything that touches the backend is
 * base64 ciphertext or non-sensitive metadata.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LS = {
    VAULT_ID: "hv_vault_id",           // deterministic hash of recovery phrase
    ENTRIES: "hv_entries_enc",
    TRASH: "hv_trash_enc",
    PIN_WRAP: "hv_pin_wrap",
    PIN_HINT: "hv_pin_hint",
    RECOVERY_WRAP: "hv_recovery_wrap",
    BIO_CREDS: "hv_bio_creds",         // JSON array of { credential_id, wrapped_key, label }
    // legacy keys (cleaned on first run post-upgrade)
    LEGACY_DEVICE_ID: "hv_device_id",
    LEGACY_BIO_WRAP: "hv_bio_wrap",
    LEGACY_BIO_CRED_ID: "hv_bio_cred_id",
    LEGACY_AUTH_META: "hv_auth_meta",
};

// ── Backend calls ──
export async function fetchVault(vaultId) {
    try {
        const r = await fetch(`${API}/vault/${encodeURIComponent(vaultId)}`);
        if (r.status === 404) return null;
        if (!r.ok) throw new Error(`fetch failed: ${r.status}`);
        return await r.json();
    } catch (e) {
        console.warn("fetchVault offline:", e.message);
        return undefined;
    }
}

export async function pushVault(vaultId, payload) {
    try {
        const r = await fetch(`${API}/vault/${encodeURIComponent(vaultId)}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        if (!r.ok) throw new Error(`push failed: ${r.status}`);
        return await r.json();
    } catch (e) {
        console.warn("pushVault offline:", e.message);
        return null;
    }
}

export async function deleteRemoteVault(vaultId) {
    try {
        await fetch(`${API}/vault/${encodeURIComponent(vaultId)}`, {
            method: "DELETE",
        });
    } catch (e) {
        console.warn("deleteRemoteVault failed:", e.message);
    }
}

// ── localStorage helpers ──
export const ls = {
    get: (k) => localStorage.getItem(k),
    set: (k, v) => localStorage.setItem(k, v),
    del: (k) => localStorage.removeItem(k),
    getJSON: (k) => {
        const v = localStorage.getItem(k);
        try {
            return v ? JSON.parse(v) : null;
        } catch {
            return null;
        }
    },
    setJSON: (k, obj) => localStorage.setItem(k, JSON.stringify(obj)),
};

// ── Bio credentials list helpers ──
export function getBioCredentials() {
    return ls.getJSON(LS.BIO_CREDS) || [];
}

export function setBioCredentials(arr) {
    if (!arr || arr.length === 0) ls.del(LS.BIO_CREDS);
    else ls.setJSON(LS.BIO_CREDS, arr);
}

// ── Hydrate local from remote ──
export function hydrateLocalFromRemote(vaultId, remote) {
    ls.set(LS.VAULT_ID, vaultId);
    if (remote.entries_encrypted) ls.set(LS.ENTRIES, remote.entries_encrypted);
    if (remote.trash_encrypted) ls.set(LS.TRASH, remote.trash_encrypted);
    if (remote.pin_wrapped_key) ls.set(LS.PIN_WRAP, remote.pin_wrapped_key);
    if (remote.pin_hint) ls.set(LS.PIN_HINT, remote.pin_hint);
    if (remote.recovery_wrapped_key)
        ls.set(LS.RECOVERY_WRAP, remote.recovery_wrapped_key);
    if (remote.bio_credentials && remote.bio_credentials.length > 0)
        ls.setJSON(LS.BIO_CREDS, remote.bio_credentials);
}

export function clearAllLocal() {
    Object.values(LS).forEach((k) => localStorage.removeItem(k));
}

// ── One-time cleanup of legacy v2.0 keys ──
export function cleanLegacyKeys() {
    [LS.LEGACY_DEVICE_ID, LS.LEGACY_BIO_WRAP, LS.LEGACY_BIO_CRED_ID, LS.LEGACY_AUTH_META].forEach(
        (k) => localStorage.removeItem(k),
    );
}
